import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import ProcessingStage from 'components/stages/video/ProcessingStage'
import IdleStage from 'components/stages/videoBeta/IdleStage'
import {
  changeCurrentAssetIndex,
  changeProcessedAssets,
  changeStage,
} from 'store/reducers/stageReducer'
import Processor, { VideoBetaStrategy } from 'api/Retomagic'
import useFetchAsset from 'hooks/query/useFetchAsset'
import useFetchGeneration from 'hooks/query/useFetchGeneration'
import useFetchStyles from 'hooks/query/useFetchStyles'
import useFetchSuggestions from 'hooks/query/useFetchSuggestions'
import { useAppDispatch, useAppSelector } from 'hooks/store'
import {
  ProcessedAssetsChangedPayload,
  ProcessorEvents,
} from 'types/ProcessorEvents'
import GenerationContainer, {
  AsideSlot,
  MainSlot,
} from '../GenerationControls/GenerationContainer/GenerationContainer'

import c from './VideoArtBeta.module.scss'

function VideoArtBeta() {
  const dispatch = useAppDispatch()

  const { stage } = useAppSelector((state) => state.stage)

  const { t } = useTranslation()

  const navigate = useNavigate()
  const { assetId, requestId } = useParams()

  const [generationProcessor, setGenerationProcessor] =
    useState<Processor | null>(null)

  const { data: stylesData } = useFetchStyles()
  const [seconds, setSeconds] = useState<number | undefined>(0)

  const { data: suggestionsData } = useFetchSuggestions()
  const { data: generationHistoryData } = useFetchGeneration()
  const { data: generatedElement } = useFetchAsset({ assetId })

  const [chosenStyle, setChosenStyle] = useState<string | undefined>('')

  const [text, setText] = useState('')
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [imageUrlParamAsset, setImageUrlParamAsset] = useState<
    string | undefined
  >('')
  const [chosenHistory, setChosenHistory] = useState<number | undefined>(
    undefined,
  )

  const imageUrl = useMemo(() => {
    if (imageFile) {
      return URL.createObjectURL(imageFile)
    }

    return null
  }, [imageFile])

  const handleProcessedAssetsChanged = useCallback(
    (payload: ProcessedAssetsChangedPayload) => {
      const { assets, isFirstSucceededResult, id } = payload

      dispatch(changeProcessedAssets(assets))
      dispatch(changeStage('idle'))

      if (isFirstSucceededResult) {
        navigate(`/video-art-beta/finish/${id}`)
      }
    },
    [],
  )

  const handleProcessingStart = useCallback(async () => {
    if (!imageFile && !chosenHistory) {
      toast.error(t('stages.generation.idle.uploadPhoto'))
      return
    }

    try {
      dispatch(changeStage('processing'))

      const thisStyle = stylesData.find((elem) => elem.id === chosenStyle)

      const strategy = new VideoBetaStrategy({
        image: chosenHistory?.toString() || imageFile!,
        request: text,
        styleTitle: thisStyle?.title || '',
        styleText: thisStyle?.text || '',
      })

      const processor = new Processor(strategy)
      processor.on(
        ProcessorEvents.PROCESSED_ASSETS_CHANGED,
        handleProcessedAssetsChanged,
      )

      await processor.start()
      setSeconds(strategy.getSeconds())
      setGenerationProcessor(processor)
    } catch (error) {
      console.log(error)
    }
  }, [handleProcessedAssetsChanged, chosenStyle, stylesData, text])

  useEffect(() => {
    if (!assetId) return
    const isInHistory = generationHistoryData?.find(
      (elem) => elem.assets[0].id === Number(assetId),
    )

    if (isInHistory) {
      setChosenHistory(Number(assetId))
    } else if (generatedElement && !isInHistory) {
      setImageUrlParamAsset(generatedElement?.url)
      setChosenHistory(Number(assetId))
    }
  }, [generatedElement, assetId])

  const handleHistoryClick = useCallback((id: number | undefined) => {
    setChosenHistory(id)
    setImageUrlParamAsset(undefined)
  }, [])

  const onImageChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (file) {
        if (file.size > 30000000) {
          toast.error(t('error.uploadPhoto'))
          return
        }

        let isCheckSize = false
        const image = URL.createObjectURL(file)
        const img = new Image()

        img.onload = () => {
          if (img.width * img.height > 10000000) {
            URL.revokeObjectURL(image)
            setImageFile(null)
            isCheckSize = true
            toast.error(t('error.uploadPhoto'))
          }
        }
        img.src = image

        if (isCheckSize) return

        setImageFile(file)
        setChosenHistory(undefined)
      }
    },
    [],
  )

  const onDeleteImage = useCallback(() => {
    setImageFile(null)
    setChosenHistory(undefined)
    // setImageFromConcept(null)
  }, [])

  useEffect(() => {
    // Сброс данных, если пользователь вернулся на главный экран
    if (stage !== 'idle') return
    setText('')
    dispatch(changeProcessedAssets([]))
    dispatch(changeCurrentAssetIndex(0))
  }, [stage])

  const onExampleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setText(event.currentTarget?.value)
    },
    [],
  )

  const onStyleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (chosenStyle === event.currentTarget.value) {
        setChosenStyle(undefined)
      } else {
        setChosenStyle(event.currentTarget.value)
      }
    },
    [chosenStyle],
  )

  useEffect(() => {
    return () => {
      if (stage === 'processing') {
        dispatch(changeProcessedAssets([]))
        dispatch(changeCurrentAssetIndex(0))
        setText('')
        generationProcessor?.stop()
        setGenerationProcessor(null)
        dispatch(changeStage('idle'))
      }
    }
  }, [generationProcessor])

  return (
    <GenerationContainer stage={stage}>
      <MainSlot>
        {stage === 'processing' && (
          <div className={c.processing}>
            <ProcessingStage seconds={seconds!} />
          </div>
        )}
        <Outlet />
      </MainSlot>
      <AsideSlot>
        <IdleStage
          text={text}
          onTextChange={setText}
          onProcessingStart={handleProcessingStart}
          onExampleClick={onExampleClick}
          onStyleClick={onStyleClick}
          chosenStyle={chosenStyle}
          suggestions={suggestionsData}
          styles={stylesData}
          imageUrl={imageUrl || imageUrlParamAsset}
          generationHistoryData={generationHistoryData}
          handleHistoryClick={handleHistoryClick}
          onDeleteImage={onDeleteImage}
          onImageChange={onImageChange}
          chosenHistory={chosenHistory}
        />
      </AsideSlot>
    </GenerationContainer>
  )
}

export default VideoArtBeta
